import React from "react";
import {
  Box,
  styled,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Paper,
} from "@mui/material";
import ReactApexChart from "react-apexcharts";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { useGetApiDataQuery } from "../../useQuery";
import { useSearchParams } from "react-router-dom";

const StyledTableCell = styled(TableCell)`
  color: #e0e0e0;
  font-size: 14px;
  font-weight: 600;
  border-bottom: 1px solid #e2e6f3;
  background: #2550d1;
`;

const StyledTableBodyCell = styled(TableCell)`
  padding: 8px 20px;
  border-bottom: 1px solid #eee;
  p {
    color: #2b3539;
    font-size: 16px;
    font-weight: 500;
    padding-right: 15px;
    min-width: 60px;
  }
`;

const StyledInfoBox = styled(Box)`
  padding: 16px 20px;
`;

const StyledTypography = styled(Typography)`
  font-size: 14px;
  font-weight: 600;
  color: #232d3d;
`;

const StyledTitle = styled(Typography)`
  color: #e0e0e0;
  font-size: 14px;
  font-weight: 600;
  padding: 16px 20px;
  border-bottom: 2px solid rgba(226, 230, 243, 0.8);
`;

const StyledInfo = styled(StyledTitle)`
  color: #232d3d;
`;

// Colors for charts
const COLOR_PALETTE = [
  "#FF9800",
  "#FFEB3B",
  "#2196F3",
  "#9C27B0",
  "#EC407A",
  "#607D8B",
  "#795548",
  "#009688",
  "#E91E63",
  "#3F51B5",
  "#8BC34A",
  "#FFC107",
  "#673AB7",
];

const STATUS_COLORS = {
  200: "#4caf50", // Green for successful calls
  201: "#4caf50",
  204: "#2196f3", // Blue for no content
  404: "#ff9800", // Orange for not found
  500: "#f44336", // Red for server errors
  403: "#9c27b0", // Purple for forbidden
};

const ApiSummary = () => {
  const [searchParams] = useSearchParams();
  const tcrid = searchParams.get("tcrid");

  const { data: apiData, isLoading, error } = useGetApiDataQuery(tcrid);

  const formatTime = (ms) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const getStatusBadge = (statusCode) => {
    return (
      <Box
        sx={{
          display: "inline-block",
          minWidth: "82px",
          padding: "8px 10px",
          borderRadius: "41px",
          backgroundColor:
            statusCode >= 200 && statusCode < 300
              ? "#e7fbe4"
              : statusCode >= 300 && statusCode < 400
                ? "#eef4fc"
                : statusCode >= 400 && statusCode < 500
                  ? "#fff3e0"
                  : "#fbe7e7",
          textAlign: "center",
          textTransform: "capitalize",
          color: "#232d3d",
          fontWeight: 600,
        }}
      >
        {statusCode}
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ padding: "24px", textAlign: "center" }}>
        <Typography>Loading API data...</Typography>
      </Box>
    );
  }

  if (error || !apiData) {
    return (
      <Box sx={{ padding: "24px", textAlign: "center" }}>
        <Typography color="error">Error loading API data</Typography>
      </Box>
    );
  }

  const { apidata = [], summarizedData = [] } = apiData;

  const statusCodeChartData = summarizedData.map((item) => ({
    name: `Status ${item.statuscode}`,
    value: item.count,
    code: item.statuscode,
  }));

  const summaryMetrics = {
    totalCalls: summarizedData.reduce((sum, item) => sum + item.count, 0),
    successfulCalls: summarizedData
      .filter((item) => item.statuscode >= 200 && item.statuscode < 300)
      .reduce((sum, item) => sum + item.count, 0),
    failedCalls: summarizedData
      .filter((item) => item.statuscode >= 400)
      .reduce((sum, item) => sum + item.count, 0),
    averageResponseTime:
      summarizedData.length > 0
        ? (
            summarizedData.reduce((sum, item) => sum + item.average_response_time * item.count, 0) /
            summarizedData.reduce((sum, item) => sum + item.count, 0)
          ).toFixed(2)
        : 0,
  };

  const successRate =
    summaryMetrics.totalCalls > 0
      ? Math.round((summaryMetrics.successfulCalls / summaryMetrics.totalCalls) * 100)
      : 0;

  return (
    <Box sx={{ padding: "24px", backgroundColor: "#f5f7f9", minHeight: "100vh" }}>
      {/* Header */}
      <Box
        display="flex"
        bgcolor="#ffffff"
        borderBottom="2px solid rgba(226, 230, 243, 0.80)"
        sx={{ borderTopRightRadius: "8px", borderTopLeftRadius: "8px" }}
      >
        <StyledInfoBox flex={1}>
          <Typography color="#232d3d" fontWeight={600}>
            API Performance Summary
          </Typography>
        </StyledInfoBox>
        <StyledInfoBox
          flex={1}
          borderRight="2px solid rgba(226, 230, 243, 0.80)"
          borderLeft="2px solid rgba(226, 230, 243, 0.80)"
        >
          <Typography color="#232d3d" fontWeight={600}>
            Test Case ID: {tcrid || "N/A"}
          </Typography>
        </StyledInfoBox>
        <StyledInfoBox flex={1}>
          <Typography color="#232d3d" fontWeight={600}>
            Total API Calls: {apidata.length}
          </Typography>
        </StyledInfoBox>
      </Box>

      {/* Summary Metrics */}
      <Box mt={4}>
        <Box bgcolor="#2550d1" sx={{ borderTopRightRadius: "8px", borderTopLeftRadius: "8px" }}>
          <StyledInfoBox>
            <Typography color="#E0E0E0" fontWeight={600}>
              Summary Metrics
            </Typography>
          </StyledInfoBox>
        </Box>
        <Box display="flex" bgcolor="#ffffff">
          <Box flex={1}>
            <Box display="flex" borderBottom="2px solid rgba(226, 230, 243, 0.80)">
              <StyledInfoBox flex={1} borderRight="2px solid rgba(226, 230, 243, 0.80)">
                <StyledTypography>Total API Calls</StyledTypography>
              </StyledInfoBox>
              <StyledInfoBox flex={2}>
                <StyledTypography>{summaryMetrics.totalCalls}</StyledTypography>
              </StyledInfoBox>
            </Box>
            <Box display="flex" borderBottom="2px solid rgba(226, 230, 243, 0.80)">
              <StyledInfoBox flex={1} borderRight="2px solid rgba(226, 230, 243, 0.80)">
                <StyledTypography>Successful Calls</StyledTypography>
              </StyledInfoBox>
              <StyledInfoBox flex={2}>
                <StyledTypography>{summaryMetrics.successfulCalls}</StyledTypography>
              </StyledInfoBox>
            </Box>
          </Box>
          <Box flex={1} borderLeft="2px solid rgba(226, 230, 243, 0.80)">
            <Box display="flex" borderBottom="2px solid rgba(226, 230, 243, 0.80)">
              <StyledInfoBox flex={1} borderRight="2px solid rgba(226, 230, 243, 0.80)">
                <StyledTypography>Failed Calls</StyledTypography>
              </StyledInfoBox>
              <StyledInfoBox flex={2}>
                <StyledTypography>{summaryMetrics.failedCalls}</StyledTypography>
              </StyledInfoBox>
            </Box>
            <Box display="flex" borderBottom="2px solid rgba(226, 230, 243, 0.80)">
              <StyledInfoBox flex={1} borderRight="2px solid rgba(226, 230, 243, 0.80)">
                <StyledTypography>Success Rate</StyledTypography>
              </StyledInfoBox>
              <StyledInfoBox flex={2}>
                <StyledTypography>{successRate}%</StyledTypography>
              </StyledInfoBox>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Summarized Data by Status Code */}
      <Box mt={4}>
        <Box bgcolor="#2550d1" sx={{ borderTopRightRadius: "8px", borderTopLeftRadius: "8px" }}>
          <StyledInfoBox>
            <Typography color="#E0E0E0" fontWeight={600}>
              Response Summary by Status Code
            </Typography>
          </StyledInfoBox>
        </Box>

        <Box display="flex" gap={3} mt={3}>
          {/* Status Code Distribution */}
          <Box
            flex={1}
            bgcolor="#fff"
            borderRadius="8px"
            overflow="hidden"
            boxShadow="0 2px 4px rgba(0,0,0,0.1)"
          >
            <Box bgcolor="#2550d1" padding="20px 24px">
              <Typography color="#E0E0E0" fontWeight={600} fontSize="16px">
                Status Code Distribution
              </Typography>
            </Box>
            <Box height={500} padding="24px">
              <ReactApexChart
                options={{
                  chart: {
                    type: "donut",
                    animations: { enabled: false },
                    fontFamily: "inherit",
                    height: "100%",
                  },
                  colors: statusCodeChartData.map(
                    (item) => STATUS_COLORS[item.code] || COLOR_PALETTE[0],
                  ),
                  labels: statusCodeChartData.map((item) => `Status ${item.code}`),
                  dataLabels: { enabled: false },
                  legend: {
                    position: "bottom",
                    offsetY: 0,
                    horizontalAlign: "center",
                    fontSize: "14px",
                    fontWeight: 500,
                    itemMargin: {
                      horizontal: 10,
                      vertical: 5,
                    },
                    markers: {
                      width: 12,
                      height: 12,
                      radius: 6,
                    },
                    formatter: function (name, opts) {
                      const value = opts.w.globals.series[opts.seriesIndex] || 0;
                      const total = opts.w.globals.seriesTotals[0];
                      const percentage = ((value / total) * 100).toFixed(1);
                      return `${name}: ${percentage}%`;
                    },
                  },
                  tooltip: {
                    y: { formatter: (val) => `${val} calls` },
                    theme: "light",
                    style: {
                      fontSize: "14px",
                    },
                    custom: function ({ series, seriesIndex, w }) {
                      const label = w.config.labels[seriesIndex];
                      return `<div class="apexcharts-tooltip-title" style="font-weight:bold; font-size:14px; padding:8px 12px;">${label}</div>
                              <div class="apexcharts-tooltip-series-group" style="padding:8px 12px;">
                                <span style="font-size:14px;">${series[seriesIndex]} calls</span>
                              </div>`;
                    },
                  },
                  plotOptions: {
                    pie: {
                      donut: {
                        size: "75%",
                        labels: {
                          show: true,
                          name: {
                            show: true,
                            fontSize: "18px",
                            fontWeight: 600,
                            offsetY: -10,
                          },
                          value: {
                            show: true,
                            fontSize: "24px",
                            fontWeight: 600,
                            offsetY: 5,
                          },
                          total: {
                            show: true,
                            label: "Total",
                            fontSize: "18px",
                            fontWeight: 600,
                            formatter: function (w) {
                              return w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                            },
                          },
                        },
                      },
                      customScale: 0.85,
                    },
                  },
                  stroke: {
                    width: 3,
                    colors: ["#fff"],
                  },
                }}
                series={statusCodeChartData.map((item) => item.value)}
                type="donut"
                height="100%"
                width="100%"
              />
            </Box>
          </Box>

          {/* Average Response Time by Status Code */}
          <Box
            flex={1}
            bgcolor="#fff"
            borderRadius="8px"
            overflow="hidden"
            boxShadow="0 2px 4px rgba(0,0,0,0.1)"
          >
            <Box bgcolor="#2550d1" padding="20px 24px">
              <Typography color="#E0E0E0" fontWeight={600} fontSize="16px">
                Average Response Time by Status Code
              </Typography>
            </Box>
            <Box height={500} padding="24px">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={summarizedData}
                  margin={{ top: 10, right: 30, left: 20, bottom: 80 }}
                  barSize={50}
                  layout="vertical"
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 14, fontWeight: 500 }}
                    axisLine={{ stroke: "#e0e0e0", strokeWidth: 1 }}
                    tickLine={{ stroke: "#e0e0e0", strokeWidth: 1 }}
                  />
                  <YAxis
                    type="category"
                    dataKey="statuscode"
                    tick={{ fontSize: 14, fontWeight: 500 }}
                    axisLine={{ stroke: "#e0e0e0", strokeWidth: 1 }}
                    tickLine={{ stroke: "#e0e0e0", strokeWidth: 1 }}
                    width={60}
                  />
                  <RechartsTooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const item = payload[0].payload;
                        return (
                          <Paper sx={{ p: 2.5, boxShadow: 3, borderRadius: 2 }}>
                            <Typography
                              variant="subtitle1"
                              fontWeight={600}
                              sx={{ mb: 1.5, fontSize: "15px" }}
                            >
                              Status Code: {item.statuscode}
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: "14px" }}>
                              Average Response Time:{" "}
                              <span style={{ fontWeight: 500 }}>
                                {formatTime(item.average_response_time)}
                              </span>
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1, fontSize: "14px" }}>
                              Count: <span style={{ fontWeight: 500 }}>{item.count}</span>
                            </Typography>
                          </Paper>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar
                    dataKey="average_response_time"
                    name="Avg Response Time (ms)"
                    radius={[0, 6, 6, 0]}
                    animationDuration={1500}
                  >
                    {summarizedData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          STATUS_COLORS[entry.statuscode] ||
                          COLOR_PALETTE[index % COLOR_PALETTE.length]
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </Box>

        {/* Summarized Data Table */}
        <Box mt={4} bgcolor="#ffffff" borderRadius="8px">
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableCell>Status Code</StyledTableCell>
                  <StyledTableCell align="center">Count</StyledTableCell>
                  <StyledTableCell align="center">Average Response Time</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summarizedData.map((item, index) => (
                  <TableRow key={index}>
                    <StyledTableBodyCell>{getStatusBadge(item.statuscode)}</StyledTableBodyCell>
                    <StyledTableBodyCell align="center">
                      <Typography sx={{ color: "#232d3d", fontWeight: 500 }}>
                        {item.count}
                      </Typography>
                    </StyledTableBodyCell>
                    <StyledTableBodyCell align="center">
                      <Typography
                        sx={{
                          color:
                            item.average_response_time < 300
                              ? "#4caf50"
                              : item.average_response_time < 1000
                                ? "#ff9800"
                                : "#f44336",
                          fontWeight: 500,
                        }}
                      >
                        {formatTime(item.average_response_time)}
                      </Typography>
                    </StyledTableBodyCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      {/* API Calls Detail */}
      <Box mt={4}>
        <Box bgcolor="#2550D1" sx={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}>
          <Typography
            color="#E0E0E0"
            fontWeight={600}
            padding="16px 20px"
            borderBottom={1}
            borderColor="gray"
          >
            API Calls Detail
          </Typography>
        </Box>

        {/* Table of API calls */}
        <TableContainer component={Box} sx={{ backgroundColor: "#ffffff" }}>
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell>URL</StyledTableCell>
                <StyledTableCell align="center">Step Number</StyledTableCell>
                <StyledTableCell align="center">Status Code</StyledTableCell>
                <StyledTableCell align="center">Response Time</StyledTableCell>
                <StyledTableCell align="center">Start Time</StyledTableCell>
                <StyledTableCell align="center">End Time</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {apidata.map((apiCall, index) => (
                <TableRow key={index}>
                  <StyledTableBodyCell>
                    <Tooltip title={apiCall.url} placement="top" arrow>
                      <Typography
                        sx={{
                          maxWidth: "600px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          color: "#232d3d",
                          fontWeight: 500,
                        }}
                      >
                        {apiCall.url}
                      </Typography>
                    </Tooltip>
                  </StyledTableBodyCell>
                  <StyledTableBodyCell align="center">
                    {(apiCall.stepnum)}
                  </StyledTableBodyCell>
                  <StyledTableBodyCell align="center">
                    {getStatusBadge(apiCall.statuscode)}
                  </StyledTableBodyCell>
                  <StyledTableBodyCell align="center">
                    <Typography
                      sx={{
                        color:
                          apiCall.responsetime < 300
                            ? "#4caf50"
                            : apiCall.responsetime < 1000
                              ? "#ff9800"
                              : "#f44336",
                        fontWeight: 500,
                      }}
                    >
                      {formatTime(apiCall.responsetime)}
                    </Typography>
                  </StyledTableBodyCell>
                  <StyledTableBodyCell align="center">
                    {new Date(apiCall.starttime).toLocaleTimeString()}
                  </StyledTableBodyCell>
                  <StyledTableBodyCell align="center">
                    {new Date(apiCall.endtime).toLocaleTimeString()}
                  </StyledTableBodyCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default ApiSummary;
